import type { ProctorSpecification } from './ProctorSpecification';
import type { ProctorAllocation } from '../native-modules/types/JSTProctorBridge';

/**
 * Applies proctor overrides to allocations, validating against specifications.
 *
 * @param specs - Array of ProctorSpecification objects to validate against
 * @param allocations - Original allocations record
 * @param override - Override allocations to apply (can be null/undefined)
 * @returns A new Record with valid overrides applied, original allocations preserved
 *
 * @example
 * const specs = [{ name: 'test_a', buckets: { control: 0, test: 1 } }];
 * const allocations = { 'test_a': { testName: 'test_a', value: 0, payload: null } };
 * const override = { 'test_a': { testName: 'test_a', value: 1, payload: null } };
 *
 * const result = applyProctorOverride(specs, allocations, override);
 * // Returns: { 'test_a': { testName: 'test_a', value: 1, payload: null } }
 */
export function applyProctorOverride(
    specs: ProctorSpecification[],
    allocations: Record<string, ProctorAllocation>,
    override: Record<string, ProctorAllocation> | undefined | null,
): Record<string, ProctorAllocation> {
    // If override is empty, null or undefined, return a copy of original allocations
    if (!override || Object.keys(override).length === 0) {
        return { ...allocations };
    }

    // Create a map of specs by name for efficient lookup
    const specsMap = new Map<string, ProctorSpecification>();
    for (const spec of specs) {
        specsMap.set(spec.name, spec);
    }

    // Start with a copy of the original allocations
    const result = { ...allocations };

    // Iterate through the override entries
    for (const [testName, overrideAllocation] of Object.entries(override)) {
        // Validate that the testName in the allocation matches the Record key
        if (overrideAllocation.testName !== testName) {
            // Override is invalid if testName doesn't match the key, skip it
            continue;
        }

        // Find the spec for this test
        const spec = specsMap.get(testName);

        if (!spec) {
            // Override contains a key that is not found in the spec, do not apply that override
            continue;
        }

        // Get valid bucket values from the spec
        const validBuckets = Object.values(spec.buckets);
        const overrideValue = overrideAllocation.value;

        if (!validBuckets.includes(overrideValue)) {
            // Override contains an invalid bucket value, do not apply the override
            continue;
        }
        const payload = overrideAllocation.payload ?? result[testName]?.payload ?? null;
        result[testName] = { ...overrideAllocation, payload };
    }

    return result;
}
